/**
 * Legal/policy pages for patient transparency: GDPR rights, HIPAA-style
 * safeguards, and a general Privacy Policy. Linked from the Landing footer,
 * the patient Profile page, and the consent step of the intake flow.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BrandLogo } from '../../components/BrandLogo';
import { ShieldCheck, Lock, FileText, ArrowLeft, Mail, Copyright } from 'lucide-react';
import { APP_BUILD_SHORT, APP_RELEASE_DATE } from '../../lib/version';

const TABS = [
  { key: 'gdpr', label: 'GDPR rights', path: '/legal/gdpr', icon: ShieldCheck },
  { key: 'hipaa', label: 'HIPAA safeguards', path: '/legal/hipaa', icon: Lock },
  { key: 'global', label: 'Global compliance', path: '/legal/global', icon: Lock },
  { key: 'privacy', label: 'Privacy policy', path: '/legal/privacy', icon: FileText },
  { key: 'copyright', label: 'Copyright', path: '/legal/copyright', icon: Copyright },
];

function Shell({ title, kicker, icon: Icon, active, children }) {
  return (
    <div className="min-h-screen section">
      <header className="surface border-b">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-back-logo">
            <BrandLogo size={48} showCopyright/>
            <span className="font-heading font-bold tracking-tight">Physiogenic</span>
          </Link>
          <Link to="/" className="text-xs text-fg-muted inline-flex items-center gap-1 hover:text-brand">
            <ArrowLeft className="w-3.5 h-3.5"/> Back
          </Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 py-12">
        <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">{kicker}</div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight inline-flex items-center gap-3">
          <Icon className="w-8 h-8 text-brand"/> {title}
        </h1>
        <p className="text-xs text-fg-muted mt-3">Last updated · 10 February 2026</p>

        <nav className="mt-8 flex flex-wrap gap-2 mb-8 border-b border-soft pb-4" data-testid="legal-tabs">
          {TABS.map(t => (
            <Link key={t.key} to={t.path}
                  className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${active === t.key ? 'bg-brand text-white border-brand' : 'border-soft text-fg-muted hover:text-fg hover:border-fg/30'}`}
                  data-testid={`legal-tab-${t.key}`}>
              <t.icon className="w-3 h-3"/> {t.label}
            </Link>
          ))}
        </nav>

        <div className="prose prose-sm max-w-none leading-relaxed text-fg-secondary [&_h2]:font-heading [&_h2]:text-fg [&_h2]:text-lg [&_h2]:mt-8 [&_h2]:mb-2 [&_h3]:font-semibold [&_h3]:text-fg [&_h3]:text-sm [&_h3]:mt-5 [&_h3]:mb-1 [&_p]:my-3 [&_li]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5">
          {children}
        </div>

        <ContactBox/>
      </article>
    </div>
  );
}

function ContactBox() {
  return (
    <div className="mt-12 surface border border-soft rounded-xl p-5">
      <div className="text-xs uppercase tracking-wider text-accent font-medium mb-1">DATA PROTECTION CONTACT</div>
      <p className="text-sm text-fg-secondary mt-2">
        For questions, rights requests, or to revoke consent, email our Data Protection Officer.
      </p>
      <a href="mailto:dpo@physiogenic.pl"
         className="mt-3 btn-primary text-xs inline-flex items-center gap-2"
         data-testid="dpo-email-link">
        <Mail className="w-3.5 h-3.5"/> dpo@physiogenic.pl
      </a>
    </div>
  );
}

export function GDPRRights() {
  return (
    <Shell title="Your GDPR rights" kicker="EU PATIENT DATA RIGHTS" icon={ShieldCheck} active="gdpr">
      <p data-testid="gdpr-intro">
        Physiogenic processes your health data under the European Union General Data Protection Regulation
        (Regulation 2016/679 — "GDPR") and Polish data protection law (UODO).
        Health data is a "special category" under <strong>GDPR Article 9</strong>; we may only process it with your
        explicit consent or where a specific legal exception (e.g. medical care) applies.
      </p>

      <h2>1. What data we process</h2>
      <ul>
        <li><strong>Identity</strong> — name, date of birth, address, PESEL (if you choose to provide it for invoicing).</li>
        <li><strong>Contact</strong> — email, phone, preferred language.</li>
        <li><strong>Health</strong> — chief complaint, intake answers, past medical history, body-chart pain points, diagnostic test results, imaging you upload, treatment plans, session notes.</li>
        <li><strong>Operational</strong> — appointment times, payment status (BLIK/cash markers — never card numbers), invoice records.</li>
        <li><strong>Technical</strong> — IP address and User-Agent are written to our audit log when you sign in or download a document.</li>
      </ul>

      <h2>2. Your rights under GDPR</h2>
      <h3>Right of access (Art. 15)</h3>
      <p>You can request a copy of every record we hold about you. We respond within 30 days at no charge.</p>
      <h3>Right to rectification (Art. 16)</h3>
      <p>You can correct any inaccurate or incomplete record directly from your patient profile, or by email.</p>
      <h3>Right to erasure / "right to be forgotten" (Art. 17)</h3>
      <p>You can request deletion of your data. We will erase what is not required by Polish health-records law (which requires us to retain medical records for 20 years from the last entry, per <em>Ustawa o prawach pacjenta</em> art. 29).</p>
      <h3>Right to restriction (Art. 18) and objection (Art. 21)</h3>
      <p>You can pause our processing of your data or object to specific uses (e.g. marketing — for which we never use medical data without your explicit consent).</p>
      <h3>Right to data portability (Art. 20)</h3>
      <p>We export your medical records as a structured PDF or JSON file you can send to another provider.</p>
      <h3>Right to withdraw consent (Art. 7)</h3>
      <p>Wherever processing relies on consent, you can withdraw it at any time without affecting prior lawful processing.</p>
      <h3>Right to lodge a complaint</h3>
      <p>You can complain to the Polish supervisory authority — <strong>Prezes Urzędu Ochrony Danych Osobowych (UODO)</strong>, ul. Stawki 2, 00-193 Warszawa, <a href="https://uodo.gov.pl" target="_blank" rel="noopener noreferrer">uodo.gov.pl</a>.</p>

      <h2>3. Legal basis for processing</h2>
      <p>We rely on your explicit consent (Art. 9(2)(a)) for the intake and treatment-planning data you give us, and on the medical-care exception (Art. 9(2)(h)) for clinical care delivered during sessions.</p>

      <h2>4. Retention</h2>
      <p>Medical records: <strong>20 years</strong> from the last entry (Polish health-records law).<br/>
         Audit logs and authentication events: <strong>2 years</strong>.<br/>
         Financial / invoice records: <strong>5 years</strong> from end of fiscal year (Polish accounting law).</p>

      <h2>5. Recipients of your data</h2>
      <p>We do not sell or rent your data. Limited categories of recipients:</p>
      <ul>
        <li>Your clinic's clinicians and admin staff (need-to-know basis, logged in audit trail).</li>
        <li>Sub-processors: <strong>Resend</strong> (transactional email), <strong>Emergent Object Storage</strong> (EU-region encrypted document storage), <strong>MongoDB Atlas</strong> (EU region).</li>
        <li>Tax authority (Polish KAS / US) when required by law for invoice reporting.</li>
      </ul>
    </Shell>
  );
}

export function HIPAASafeguards() {
  return (
    <Shell title="HIPAA-aligned safeguards" kicker="ADMINISTRATIVE · PHYSICAL · TECHNICAL" icon={Lock} active="hipaa">
      <p>
        Physiogenic operates from Poland under GDPR. We are not a HIPAA "covered entity" or "business associate", but
        we voluntarily adopt the spirit of the <strong>HIPAA Security Rule (45 CFR §164.300 et seq.)</strong> as a
        higher bar for medical-data security. This document maps our controls to the three HIPAA safeguard families.
      </p>

      <h2>1. Administrative safeguards</h2>
      <ul>
        <li><strong>Access control</strong> — role-based (patient / clinician / admin). Every action gated by the role check is logged.</li>
        <li><strong>Workforce training</strong> — annual GDPR + medical-data handling refresher for all clinic staff.</li>
        <li><strong>Audit trail</strong> — every login, document download, and record edit writes an immutable row in the audit log (IP, user, timestamp, action).</li>
        <li><strong>Incident response</strong> — 72-hour GDPR breach notification window; dedicated DPO contact.</li>
        <li><strong>Business associate agreements</strong> — signed with all sub-processors handling identifiable health data.</li>
      </ul>

      <h2>2. Physical safeguards</h2>
      <ul>
        <li><strong>EU-region data centers</strong> — all databases and object storage hosted in EU (no transatlantic transfers of identifiable data).</li>
        <li><strong>Provider compliance</strong> — sub-processors are ISO 27001 / SOC 2 certified.</li>
        <li><strong>Workstation security</strong> — clinic devices require disk encryption + auto-lock after 5 minutes idle.</li>
      </ul>

      <h2>3. Technical safeguards</h2>
      <ul>
        <li><strong>Encryption in transit</strong> — TLS 1.3 for all client/server traffic; HSTS enforced.</li>
        <li><strong>Encryption at rest</strong> — AES-256 for medical document storage; bcrypt for password hashes.</li>
        <li><strong>Authentication</strong> — JWT access tokens + httpOnly cookies; optional TOTP 2FA for clinicians and admins.</li>
        <li><strong>Authorization</strong> — every record query is scoped to the patient's user_id; cross-patient access requires audited admin role.</li>
        <li><strong>Integrity</strong> — append-only audit log; document storage exposes ETag for tamper-evidence.</li>
        <li><strong>Automatic logoff</strong> — 12-hour JWT lifetime (configurable down to 1 hour for high-security clinics).</li>
      </ul>

      <h2>4. Breach notification</h2>
      <p>
        In the event of a confirmed security incident affecting your personal or health data, we will notify you and
        the Polish supervisory authority (UODO) within <strong>72 hours of discovery</strong>, per GDPR Article 33.
      </p>

      <h2>5. Your access rights</h2>
      <p>
        You can review, download, and request correction or deletion of your medical records at any time from your
        patient profile — same workflow as under GDPR.
      </p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy policy" kicker="HOW WE USE YOUR INFORMATION" icon={FileText} active="privacy">

      <h2>1. Data controller & contact</h2>
      <p>
        <strong>Physiogenic</strong> (operating under the company designated by the clinic) is the
        <em> Data Controller</em> of your personal data for the purposes of the EU General Data Protection
        Regulation (GDPR) and the U.S. Health Insurance Portability and Accountability Act (HIPAA). The clinic
        operating name, registered address and Polish KRS / NIP numbers are visible on every invoice.
      </p>
      <p>
        <strong>Privacy Contact Officer (DPO):</strong> all privacy questions, access requests, and complaints can
        be addressed to <a href="mailto:dpo@physiogenic.pl" className="text-brand underline">dpo@physiogenic.pl</a>.
        We acknowledge requests within 7 days and respond within 30 days (extendable by a further 60 days for
        complex cases, per GDPR Art. 12).
      </p>

      <h2>2. Information we collect</h2>
      <h3>Personal details</h3>
      <ul>
        <li>Full legal name, date of birth, address, phone, email.</li>
        <li>Government identifier (PESEL in Poland, NHS number in the UK, equivalent elsewhere) — only where required for medical billing.</li>
        <li>Profile photo (optional, base64-stored, used only inside the patient portal).</li>
      </ul>
      <h3>Sensitive health data (GDPR Art. 9 / HIPAA PHI)</h3>
      <ul>
        <li>Medical history, current symptoms and conditions, medications, allergies.</li>
        <li>Imaging, lab results, referral letters and other documents you upload.</li>
        <li>Treatment plans, SOAP notes, objective assessment data (ROM, MMT, neurology, balance, cardio).</li>
        <li>Outcome measure scores (Oswestry, QuickDASH and the like) and rehabilitation progress notes.</li>
      </ul>
      <h3>Other data</h3>
      <ul>
        <li>GP / referring physician details, insurance information, emergency contact.</li>
        <li>Financial / payment details (invoices, partial payments, Stripe payment session IDs — full card numbers are <strong>never</strong> stored on our servers).</li>
        <li>Authentication and security data (hashed passwords, login timestamps, IP address for audit-log entries).</li>
      </ul>

      <h2>3. Purposes of collection & processing</h2>
      <p>We collect personal and health data <strong>only</strong> for the following specific purposes:</p>
      <ul>
        <li><strong>Clinical care</strong> — providing safe, effective physiotherapy assessment and treatment.</li>
        <li><strong>Administration</strong> — appointment scheduling, reminders (24h / 2h), reschedules, cancellations.</li>
        <li><strong>Billing & accounting</strong> — invoice generation, Polish VAT-exemption reporting (medical service art. 43.1.19), KSeF e-invoicing where required.</li>
        <li><strong>Communication</strong> — pre-visit instructions, post-session recovery tips, marketing emails (opt-in only, with one-click unsubscribe).</li>
        <li><strong>Legal obligations</strong> — record-retention required by Polish health-records law, anti-money-laundering checks where applicable.</li>
      </ul>
      <p>We will not use your data for any purpose beyond those listed above without obtaining fresh consent.</p>

      <h2>4. Consent & legal basis</h2>
      <p>
        At sign-up you provide <strong>explicit consent</strong> (digital signature, GDPR Art. 7) for us to process
        your personal and health data. You can <strong>withdraw consent at any time</strong> from your patient
        profile; withdrawal does not affect the lawfulness of processing already carried out.
      </p>
      <p>Where consent is not the basis, processing is justified by one of:</p>
      <ul>
        <li><strong>Necessity for the provision of medical care</strong> (GDPR Art. 9.2.h) — performing physiotherapy under contract.</li>
        <li><strong>Compliance with a legal obligation</strong> (GDPR Art. 6.1.c) — tax records, health-records retention, professional reporting.</li>
        <li><strong>Vital interests</strong> (GDPR Art. 6.1.d) — emergency disclosure to next-of-kin or emergency services if your life is at risk.</li>
      </ul>

      <h2>5. Data sharing & third-party disclosures</h2>
      <p>We may share <strong>specific</strong> elements of your record under the following circumstances:</p>
      <ul>
        <li><strong>Your GP, referring physician, or hospital</strong> — only with your <em>explicit written consent</em>, or when legally required (e.g. court order).</li>
        <li><strong>Private health insurers</strong> — for claim reimbursement, only after you sign the corresponding assignment-of-benefits form.</li>
        <li><strong>Public health authority (UODO, NFZ, sanitary epidemiologist)</strong> — when required by Polish public-health or data-protection law.</li>
        <li><strong>Sub-processors</strong> (listed below) — strictly for the technical operation of the service, bound by a Data Processing Agreement.</li>
      </ul>
      <h3>Sub-processors</h3>
      <ul>
        <li><strong>Resend (US)</strong> — transactional email delivery (welcome, reminders, invoice PDFs). SCCs in place.</li>
        <li><strong>Emergent Object Storage (EU)</strong> — encrypted medical document storage. EU-region, no transatlantic transfer.</li>
        <li><strong>MongoDB Atlas (EU)</strong> — primary database, hosted in EU central region.</li>
        <li><strong>Stripe (EU / US)</strong> — payment processing. Card details handled directly by Stripe — we only store an anonymised session ID.</li>
        <li><strong>Backblaze B2 (EU)</strong> — encrypted off-site backups for disaster recovery.</li>
        <li><strong>Anthropic / OpenAI / Google AI (US)</strong> — large-language-model inference for red-flag screening and treatment-plan suggestions. <em>Only de-identified clinical summaries are sent.</em> Full records are never transmitted.</li>
      </ul>
      <h3>Overseas transfers</h3>
      <p>
        Identifiable health data stays inside the EU. Any non-identifiable summaries that leave the EU (for AI
        analysis, for example) are transferred under <strong>Standard Contractual Clauses</strong> and the
        respective providers' EU adequacy schemes.
      </p>

      <h2>6. Data storage, security & integrity</h2>
      <h3>Physical security</h3>
      <ul>
        <li>Paper records — kept in locked cabinets in our Warsaw office; only the treating clinician and admin staff hold keys.</li>
        <li>Clinic devices — disk-encrypted; auto-lock after 5 minutes idle; biometric or passphrase unlock.</li>
      </ul>
      <h3>Technical security</h3>
      <ul>
        <li>TLS 1.3 for all client/server traffic; HSTS enforced.</li>
        <li>AES-256 at rest for medical documents; bcrypt for password hashes.</li>
        <li>HttpOnly + SameSite cookies; CSRF anti-forgery tokens.</li>
        <li>Optional TOTP-based two-factor authentication for every role (admin, clinician, reception, patient).</li>
        <li>Daily off-site encrypted backups; 90-day point-in-time recovery.</li>
      </ul>
      <h3>Staff training</h3>
      <p>
        Every clinician and admin completes an annual GDPR + HIPAA + medical-confidentiality refresher. New
        staff cannot access patient records until they sign a confidentiality agreement and complete the training.
      </p>

      <h2>7. Data retention & destruction</h2>
      <ul>
        <li><strong>Clinical records</strong> — 20 years from the last visit (Polish health-records law); separate 30-year retention for paediatric records (kept until age 25).</li>
        <li><strong>Financial / invoice records</strong> — 5 years from the end of the tax year (Polish accounting law).</li>
        <li><strong>Audit logs</strong> — 90 days hot storage, then archived for 2 years and securely deleted.</li>
        <li><strong>Marketing communications</strong> — until you unsubscribe; the unsubscribe record itself is kept for 3 years to honour your wishes.</li>
      </ul>
      <p>
        When records reach end-of-life they are <strong>securely destroyed</strong>: paper documents are
        cross-cut shredded by a certified provider with a Certificate of Destruction; digital records are
        cryptographically erased (Crypto-Shredding NIST SP 800-88).
      </p>

      <h2>8. Your rights</h2>
      <p>Under GDPR Art. 15–22 you have the right to:</p>
      <ul>
        <li><strong>Access</strong> — request a copy of your record (returned within 30 days, machine-readable JSON + PDF).</li>
        <li><strong>Rectification</strong> — correct any inaccurate information.</li>
        <li><strong>Erasure</strong> ("right to be forgotten") — request deletion, subject to legal retention obligations above.</li>
        <li><strong>Restriction</strong> — pause processing while a dispute is resolved.</li>
        <li><strong>Portability</strong> — receive your data in a structured, commonly-used format and transfer it to another controller.</li>
        <li><strong>Object</strong> — to processing based on legitimate interest or for direct marketing.</li>
        <li><strong>Not be subject to automated decision-making</strong> — our AI red-flag screening is advisory only; the clinician makes every clinical decision.</li>
      </ul>
      <p>Exercise any of these rights from <em>Profile → Privacy & Data Rights</em>, or by emailing the DPO above.</p>

      <h2>9. Complaints</h2>
      <p>
        If you believe your information has been mishandled, please first contact our DPO (<a href="mailto:dpo@physiogenic.pl" className="text-brand underline">dpo@physiogenic.pl</a>) so we can investigate.
        If you remain unsatisfied, you have the right to lodge a complaint with the Polish supervisory authority:
      </p>
      <ul>
        <li>
          <strong>Urząd Ochrony Danych Osobowych (UODO)</strong><br/>
          ul. Stawki 2, 00-193 Warszawa, Poland<br/>
          tel: +48 22 531 03 00 · <a href="https://uodo.gov.pl" className="text-brand underline">uodo.gov.pl</a>
        </li>
      </ul>
      <p>Outside Poland you may also complain to your national supervisory authority (ICO in the UK, CNIL in France, AEPD in Spain, etc.) or to the U.S. Department of Health & Human Services Office for Civil Rights for HIPAA-related concerns.</p>

      <h2>10. Updates to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be announced in your
        patient inbox at least <strong>30 days before</strong> they take effect, and (where required) we will
        request fresh consent before continuing to process your data under the new terms. Non-material
        clarifications take effect immediately and are noted in the change log at the bottom of this page.
      </p>
      <p className="text-xs text-fg-muted mt-6">
        Change log · <em>2026-02-14:</em> expanded sections 1–10 to full GDPR + HIPAA detail.
        <em> 2026-02-10:</em> initial publication.
      </p>
    </Shell>
  );
}

export function CopyrightPage() {
  return (
    <Shell title="Copyright & IP" kicker="INTELLECTUAL PROPERTY" icon={Copyright} active="copyright">
      <p data-testid="copyright-tagline">
        <strong>Physiogenic</strong> — Clinic management for physiotherapy, osteopathy,
        chiropractic, and massage therapy.
      </p>
      <p>© 2024–2026 Physiogenic. All rights reserved.</p>
      <p className="text-xs text-fg-muted">Current release: <strong data-testid="copyright-version">{APP_BUILD_SHORT}</strong> · {APP_RELEASE_DATE}</p>

      <h2>1. Software ownership</h2>
      <p>
        The Physiogenic source code, design, brand assets, documentation, and database
        schemas (the "Software") are the exclusive intellectual property of Physiogenic.
        The Software is licensed — not sold — to subscribed clinics under our Subscription Terms.
      </p>
      <p>You may not, without prior written consent:</p>
      <ul>
        <li>Copy, modify, merge, publish, distribute, sublicense or sell the Software.</li>
        <li>Reverse-engineer, decompile, or disassemble the Software.</li>
        <li>Remove or alter the copyright notice or trademark in any delivered files.</li>
        <li>Use the Physiogenic name, logo or brand marks except as expressly permitted by your subscription.</li>
      </ul>

      <h2>2. Clinic data ownership</h2>
      <p>
        Patient and clinical data that <strong>you</strong> create remain your property
        (subject to local healthcare data-protection law — GDPR/RODO in the EU,
        HIPAA in the US). Physiogenic processes those data strictly to provide the
        Software per the Data Processing Addendum. On account closure, you may
        export your data at any time; we delete it after the contractual retention
        window expires.
      </p>

      <h2>3. Third-party components</h2>
      <p>
        The Software incorporates open-source libraries listed in our public NOTICES file.
        Each retains its respective license. ICD-10® and ICD-9-CM® are trademarks of
        the World Health Organization; their use here is purely for clinical classification.
      </p>

      <h2>4. Trademarks</h2>
      <p>
        <strong>Physiogenic</strong> and the Physiogenic logo are trademarks of Physiogenic.
        All other marks are property of their respective owners.
      </p>

      <h2>5. Contact</h2>
      <p>
        Copyright inquiries: <a href="mailto:legal@physiogenic.clinic" className="text-brand hover:underline">legal@physiogenic.clinic</a><br/>
        DMCA / takedown: <a href="mailto:dmca@physiogenic.clinic" className="text-brand hover:underline">dmca@physiogenic.clinic</a>
      </p>
    </Shell>
  );
}

export function GlobalCompliance() {
  return (
    <Shell title="Global compliance" kicker="WORLDWIDE PATIENT DATA PROTECTION" icon={Lock} active="global">
      <p data-testid="global-intro">
        Physiogenic operates across the globe. Wherever your clinic and patients are based, we map the
        platform's behaviour to the strictest privacy + medical-record laws that apply. This page summarises
        the frameworks we comply with — and what each means for you in practice.
      </p>

      <h2>Privacy frameworks we comply with</h2>
      <ul>
        <li><strong>European Union — GDPR (Regulation 2016/679)</strong>. Articles 7, 12, 15, 17, 20 are all self-service from <code>/account/security</code>.</li>
        <li><strong>United Kingdom — UK GDPR + Data Protection Act 2018</strong>. Equivalent to EU GDPR; ICO is the supervisory authority.</li>
        <li><strong>United States — HIPAA + HITECH</strong> for protected health information; <strong>CCPA / CPRA</strong> for California residents; state-level breach notification laws.</li>
        <li><strong>Canada — PIPEDA</strong> federally + provincial laws (Quebec Law 25, Ontario PHIPA, Alberta PIPA).</li>
        <li><strong>Australia — Privacy Act 1988</strong> + Australian Privacy Principles (APPs), My Health Records Act 2012.</li>
        <li><strong>New Zealand — Privacy Act 2020 + Health Information Privacy Code 2020</strong>.</li>
        <li><strong>Brazil — LGPD (Lei Geral de Proteção de Dados)</strong>. Equivalent self-service rights to GDPR.</li>
        <li><strong>Switzerland — Federal Act on Data Protection (FADP, 2023 revision)</strong>.</li>
        <li><strong>Singapore — PDPA (Personal Data Protection Act 2012)</strong>.</li>
        <li><strong>UAE — DIFC Data Protection Law No. 5 of 2020</strong>.</li>
        <li><strong>South Africa — POPIA (Protection of Personal Information Act 4 of 2013)</strong>.</li>
        <li><strong>India — Digital Personal Data Protection Act 2023</strong>.</li>
        <li><strong>Japan — APPI (Act on the Protection of Personal Information)</strong>.</li>
      </ul>

      <h2>Medical record retention — varies by country</h2>
      <p>
        Patient records cannot be deleted before the retention window expires, even on a patient's request.
        We honour the strictest applicable law:
      </p>
      <ul>
        <li>🇵🇱 Poland: <strong>20 years</strong> (Act on Patient Rights, Art. 29)</li>
        <li>🇫🇷 France: <strong>20 years</strong> (Code de la santé publique R.1112-7)</li>
        <li>🇩🇪 Germany: <strong>10 years</strong> (Patientenrechtegesetz §630f)</li>
        <li>🇬🇧 UK: <strong>8–10 years</strong> (NHS Records Management Code 2021)</li>
        <li>🇪🇸 Spain: <strong>5 years</strong> (Ley 41/2002 Art. 17)</li>
        <li>🇺🇸 US: <strong>6 years (HIPAA minimum)</strong>; 7–10 years in most states</li>
        <li>🇨🇦 Canada: <strong>10 years</strong> (varies by province; Ontario PHIPA: 10)</li>
        <li>🇦🇺 Australia: <strong>7 years</strong> (adult); until 25 (child)</li>
        <li>🇳🇿 New Zealand: <strong>10 years</strong> (Health (Retention of Health Information) Regulations)</li>
        <li>🇮🇪 Ireland: <strong>8 years</strong> (HSE Health Records Management Code)</li>
      </ul>

      <h2>What we do — regardless of jurisdiction</h2>
      <ul>
        <li><strong>Data residency</strong>: every clinic's data sits in the EU (Frankfurt) by default; US/UK/AU regions are available on request for sovereignty-sensitive contracts.</li>
        <li><strong>Encryption</strong>: TLS 1.3 in transit; AES-256 at rest. Patient documents + AI scribe audio are bucket-encrypted with per-clinic keys.</li>
        <li><strong>Granular consent (7 keys)</strong>: marketing email, AI scribe, anonymised research, GP outcome sharing, internal clinical audit, photo/video, emergency contact sharing.</li>
        <li><strong>Self-service</strong>: every patient on every plan can download all their data + delete their account from <code>/patient/account</code>.</li>
        <li><strong>Breach notification</strong>: 72 hours for EU/UK/CH/BR; "without unreasonable delay" for US/CA/AU/NZ. We notify clinics in &lt;6 hours regardless.</li>
        <li><strong>Sub-processors</strong>: Stripe (payments), Resend (email), Backblaze B2 (backups), OpenAI/Anthropic/Google (AI). All under DPAs.</li>
        <li><strong>No data sale, ever</strong>: patient health information is never sold, traded, or used for marketing outside your clinic.</li>
      </ul>

      <h2>Country-specific clinic features</h2>
      <p>
        Invoice formats, tax exemptions, and government ID fields auto-render based on your clinic's
        <code>country_code</code>. Examples:
      </p>
      <ul>
        <li><strong>PL</strong>: NIP/REGON on invoices, KSeF-ready footer, VAT-exempt under VAT Act Art. 43(1)(19), PESEL on patient profile.</li>
        <li><strong>DE</strong>: USt-IdNr on invoices, VAT-exempt under UStG §4 Nr. 14, Versichertennummer on patient profile.</li>
        <li><strong>FR</strong>: SIRET on invoices, VAT-exempt under CGI Art. 261-4-1°, INS-NIR on patient profile.</li>
        <li><strong>UK</strong>: VAT Reg # on invoices, generally VAT-exempt for medical care, NHS number on patient profile.</li>
        <li><strong>US</strong>: EIN on invoices, state-by-state sales tax handling, SSN/Medicare # on patient profile.</li>
        <li><strong>ES</strong>: NIF/CIF on invoices, VAT-exempt under Ley 37/1992 Art. 20.Uno.3°.</li>
      </ul>

      <p style={{marginTop: '2rem'}}>
        If your country isn't listed, contact us — we add new jurisdictions on a 7-day SLA for paying clinics.
      </p>
    </Shell>
  );
}

export default function Legal() {
  // Default to GDPR if someone hits /legal directly.
  const { pathname } = useLocation();
  if (pathname === '/legal' || pathname === '/legal/') {
    return <GDPRRights/>;
  }
  return null;
}
